import React, { useCallback, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InfoCircleOutlined as Info } from '@ant-design/icons';
import { Alert, Breadcrumb, Col, Row } from 'antd';

import Product from 'classes/Product';
import AlertComponent from 'components/Alert';
import { EProductStatus } from 'helpers/enums';
import type IProduct from 'interfaces/IProduct';
import Delivery from 'scenes/Producer/Product/components/InfoDeliveryMethods';
import DeliveryValidations from 'scenes/Producer/Product/components/InfoDeliveryMethods/validations';
import General from 'scenes/Producer/Product/NewProduct/components/Forms/General';
import GeneralValidations from 'scenes/Producer/Product/NewProduct/components/Forms/General/validations';
import Sales from 'scenes/Producer/Product/NewProduct/components/Forms/Sales';
import SalesSubscriptionValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/components/Subscription/validations';
import SalesValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/validations';
import RecoverySalesValidation from 'scenes/Producer/Product/NewProduct/components/Forms/SalesRecovery/validations';
import TopPage from 'v2/components/Shared/TopPage';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { useCategories } from 'v2/hooks/products/useCategories';
import { useValidateProductTitle } from 'v2/hooks/products/useValidateProductTitle';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import { EChargeType, EProductType } from 'v2/interfaces/product';
import chatBotService, { Activity } from 'v2/services/chatBot';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import productService from 'v2/services/product';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import DraftButtons from '../../components/DraftButtons';
import { NewProductHint } from '../../components/Hints/NewProductHint';
import { PrivateLabelRights } from '../../components/PrivateLabelRights';
import SalesRecovery from '../components/Forms/SalesRecovery';
import { useWizardState } from '../contexts/WizardStateContext';
import { scrollToInvalidField } from '../helpers/scrollToInvalidField';

interface IProps {
  isSubscription?: boolean;
}

function InfoProduct(props: IProps) {
  const history = useHistory();
  const { isSubscription } = props;
  const { previousStep, updateLoading, resetLoading, setProduct: setWizardProduct, nextStep } = useWizardState();
  const { categories } = useCategories();
  const { productTitleExists, validateTitle: validateTitleForSave } = useValidateProductTitle();
  const sendPostMessage = usePostMessage();

  const { user } = useUserStore(({ user }) => ({ user }));

  const [product, setProduct] = useState<IProduct>(
    isSubscription
      ? new Product({ charge_type: EChargeType.SUBSCRIPTION, type: EProductType.DIGITAL, sales_recovery: true })
      : new Product({ undetermined_charges: true, sales_recovery: true })
  );
  const [enabledValidation, setEnabledValidation] = useState(false);

  const errorsRef = useRef<any>({});
  const fieldsRef = useRef<any>({});

  const scrollToInvalidFields = useCallback(
    (
      generalErrors: any,
      generalRequiredFields: number,
      salesRequiredFields: number,
      deliveryRequiredFields: number,
      shouldScroll: boolean = true
    ) => {
      if (!shouldScroll) {
        return;
      }

      if (generalRequiredFields > 0) {
        const prop = Object.keys(generalErrors)[0];
        scrollToInvalidField(fieldsRef.current[prop]);
        return;
      }

      if (salesRequiredFields > 0) {
        scrollToInvalidField(fieldsRef.current['sales']);
        return;
      }

      if (deliveryRequiredFields) {
        scrollToInvalidField(fieldsRef.current['deliveries']);
      }
    },
    []
  );

  const isFormValid = useCallback(
    (productToValidate: IProduct, scroll: boolean = true): boolean => {
      const GeneralErrors = run(productToValidate, GeneralValidations);
      const generalRequiredFields = GeneralErrors ? Object.keys(GeneralErrors).length : 0;
      let salesRequiredFields = 0;

      if (productToValidate?.charge_type === 'N') {
        const e = run(productToValidate, SalesValidations);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      if (productToValidate?.sales_recovery) {
        const e = run(productToValidate, RecoverySalesValidation);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      if (productToValidate?.charge_type === 'A') {
        const e = run(productToValidate, SalesSubscriptionValidations);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      const isServiceType = productToValidate.type === EProductType.SERVICE;
      const DeliveryErrors = isServiceType ? null : run(productToValidate, DeliveryValidations);
      const deliveryRequiredFields = DeliveryErrors ? Object.keys(DeliveryErrors).length : 0;
      scrollToInvalidFields(GeneralErrors, generalRequiredFields, salesRequiredFields, deliveryRequiredFields, scroll);
      return generalRequiredFields === 0 && salesRequiredFields === 0 && deliveryRequiredFields === 0;
    },
    [scrollToInvalidFields]
  );

  const setRefField = useCallback((name: any, ref: any) => {
    fieldsRef.current[name] = ref;
  }, []);
  const setRefError = useCallback((name: any, ref: any) => {
    errorsRef.current[name] = ref;
  }, []);

  const handleChange = useCallback((name: string, value: any) => {
    setProduct(prevProduct => {
      const currentProduct = { ...prevProduct };
      (currentProduct as any)[name] = value;
      return new Product(currentProduct);
    });
  }, []);

  const handleProductType = useCallback((name: string, value: string) => {
    setProduct(prevProduct => {
      const currentProduct = { ...prevProduct };
      (currentProduct as any)[name] = value === 'service' ? EProductType.SERVICE : EProductType.DIGITAL;
      return new Product(currentProduct);
    });
  }, []);

  const resetDeliveries = useCallback(() => {
    setProduct(prevProduct => {
      const productCopy = new Product(prevProduct);
      productCopy.deliveries = [];
      return productCopy;
    });
  }, []);

  const handleChangeDeliveries = useCallback((deliveries: any) => {
    setProduct(prevProduct => {
      const productCopy = new Product(prevProduct);
      productCopy.setDeliveries(deliveries);
      return productCopy;
    });
  }, []);

  const onUpload = useCallback((uploadId: number) => {
    setProduct(prevProduct => {
      const productCopy = new Product(prevProduct);
      productCopy.private_label_rights = uploadId;
      return productCopy;
    });
  }, []);

  const onRemoveUpload = useCallback(() => {
    setProduct(prevProduct => {
      const productCopy = new Product(prevProduct);
      productCopy.private_label_rights = 0;
      return productCopy;
    });
  }, []);

  const backWizard = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      previousStep();
    },
    [previousStep]
  );

  const handleSubmit = useCallback(
    (isDraft: boolean = false) => {
      const productToSubmit = product;

      if (isDraft) {
        productToSubmit.active = EProductStatus.DRAFT;
      }

      resetLoading();

      if (!isDraft && !isFormValid(productToSubmit)) {
        setEnabledValidation(true);
        toast.error('Por favor preencha todos os campos obrigatórios');
        return;
      }

      (async () => {
        try {
          updateLoading(33);
          const createdData = await productService.createProduct(productToSubmit.serviceFormat());
          const prod = createdData?.product ?? createdData;

          if (!prod?.id) {
            toast.error('Erro ao criar o produto');
            resetLoading();
            return;
          }

          updateLoading(66);

          if (isDraft) {
            sendPostMessage({
              source: PostMessageSource.PRODUCT_MANAGER,
              action: PostMessageAction.PRODUCT_CREATED,
              data: {
                product_id: prod.id,
                is_draft: true,
                completed_at: new Date().toISOString(),
                product_type: productToSubmit.type,
                product_type_title: getProductTypeTitle(productToSubmit.type)
              }
            });
            toast.success('Seu produto foi salvo como rascunho!');
            history.push('/producer/product/myproducts');
            return;
          }

          const loadResult: any = await productService.loadProductById(prod.id, false);
          const newProd = new Product(loadResult?.data ?? loadResult);
          newProd.setDeliveries(productToSubmit?.deliveries || []);
          setWizardProduct(newProd);
          updateLoading(100);
          resetLoading();
          nextStep();

          if (!user.producer) {
            eduzzAccountsService.loadAccountDetails();
          }
        } catch (error) {
          toast.error(error);
          resetLoading();
          return;
        }

        chatBotService.activity(Activity.productRegistered);
      })();
    },
    [
      product,
      isFormValid,
      user.producer,
      history,
      resetLoading,
      updateLoading,
      setWizardProduct,
      nextStep,
      sendPostMessage
    ]
  );

  const handleClickSubmitDraft = useCallback(async () => {
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será salvo como rascunho. Para publicar, volte a editar esse produto.',
      message: 'O seu produto não poderá ser vendido enquanto você não publicá-lo para venda. Deseja Continuar?',
      confirmation: true,
      buttonOk: 'Confirmar e salvar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit(true);
    }
  }, [handleSubmit]);

  const handleClickSubmit = useCallback(async () => {
    if (!isFormValid(product)) {
      setEnabledValidation(true);
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }

    const message = (
      <>
        Concordo que meu produto está em conformidade com as recomendações para evitar interrupções nas vendas durante o
        processo de análise e moderação.
        <Alert
          icon={<Info />}
          type='info'
          className='mt-3'
          message={
            <>
              Caso seja reprovado na moderação, seu produto ficará indisponível para venda.{' '}
              <a
                href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807655450-Como-funciona-a-modera%C3%A7%C3%A3o-de-conte%C3%BAdo'
                rel='noopener noreferrer'
                target='_blank'
              >
                Saiba mais
              </a>
            </>
          }
        />
      </>
    );
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será enviado para a moderação, mas não se preocupe, você já pode começar a vender.',
      message,
      confirmation: true,
      buttonOk: 'Confirmar e cadastrar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit();
    }
  }, [handleSubmit, isFormValid, product]);

  const canSaveDraft = !!product.title;

  return (
    <Row id='new-infoproduct' gutter={[16, 16]}>
      <Col span={24}>
        <Breadcrumb
          items={[
            { title: <Link to='/producer/product/myproducts'>Meus Produtos</Link> },
            {
              title: (
                <a href='#' style={{ cursor: 'pointer' }} onClick={backWizard}>
                  Tipo de produto
                </a>
              )
            },
            { title: isSubscription ? 'Nova assinatura' : 'Novo infoproduto' }
          ]}
        />
      </Col>
      <Col span={24}>
        <TopPage
          title={isSubscription ? 'Nova Assinatura' : 'Novo Infoproduto'}
          helpLink='https://ajuda.eduzz.com/hc/pt-br/articles/4403015162139-O-que-é-um-infoproduto-digital-e-como-cadastrar-'
        />
      </Col>

      <Col md={18} xs={24}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <General
              setRefField={setRefField}
              setRefError={setRefError}
              product={product}
              handleChange={handleChange}
              categoryList={categories}
              validateTitleForSave={validateTitleForSave}
              productTitleExists={productTitleExists}
              enabledValidation={enabledValidation}
              {...(isSubscription && {
                handleProductType,
                resetDeliveries,
                hasDeliveries: product?.deliveries?.length > 0
              })}
            />
          </Col>
          <Col span={24}>
            <Sales
              ref={(ref: any) => setRefField('sales', ref)}
              product={product}
              handleChange={handleChange}
              setRefField={setRefField}
              setRefError={setRefError}
              enabledValidation={enabledValidation}
              lockedChargeType={isSubscription}
            />
          </Col>
          <Col span={24}>
            <SalesRecovery handleChange={handleChange} product={product} enabledValidation={enabledValidation} />
          </Col>
          <Col span={24}>
            <Delivery
              ref={(ref: any) => setRefField('deliveries', ref)}
              newProduct={true}
              fade={true}
              deliveries={product ? product.deliveries : []}
              onChange={handleChangeDeliveries}
              isInfo={product.type !== EProductType.SERVICE}
              isService={product.type === EProductType.SERVICE}
              offer={product}
              enabledValidation={enabledValidation}
            />
          </Col>
          {product.type !== EProductType.SERVICE && (
            <Col span={24}>
              <PrivateLabelRights onUpload={onUpload} onRemove={onRemoveUpload} />
            </Col>
          )}
          <Col span={24}>
            <DraftButtons
              disabled={{ tooltip: false, button: false }}
              disabledDraft={{ tooltip: !!canSaveDraft, button: !canSaveDraft }}
              onSubmit={() => handleClickSubmit()}
              onSubmitDraft={() => handleClickSubmitDraft()}
              isService={product.type === EProductType.SERVICE}
            />
          </Col>
        </Row>
      </Col>
      <Col md={6} xs={24}>
        <NewProductHint type={product.type === EProductType.SERVICE ? 'service' : 'digital'} />
      </Col>
    </Row>
  );
}

export default InfoProduct;
